<template>
  <div
    class="data-configuration-erp-dropdown"
    :data-testid="dataTestid"
  >
    <v-menu
      ref="menu"
      v-model="menuOpen"
      :close-on-content-click="false"
      :disabled="disabled || readonly"
      offset-y
      :nudge-bottom="4"
      :z-index="1000"
      :max-width="menuWidth"
      :min-width="menuWidth"
      content-class="data-configuration-erp-dropdown__menu"
      :data-testid="`${dataTestid}__menu`"
    >
      <template #activator="{ on, attrs }">
        <div
          ref="activator"
          class="data-configuration-erp-dropdown__field"
          :class="{
            'data-configuration-erp-dropdown__field--disabled': disabled,
            'data-configuration-erp-dropdown__field--readonly': readonly && !disabled,
            'data-configuration-erp-dropdown__field--placeholder': !value,
          }"
          :data-testid="`${dataTestid}__field`"
          role="button"
          v-bind="attrs"
          :aria-disabled="disabled || readonly"
          :aria-readonly="readonly"
          :tabindex="disabled || readonly ? -1 : 0"
          v-on="disabled || readonly ? {} : on"
          @keydown="onActivatorKeydown($event, on)"
        >
          <span class="data-configuration-erp-dropdown__value">
            {{ value || placeholder }}
          </span>
          <v-icon class="data-configuration-erp-dropdown__chevron" small>mdi-menu-down</v-icon>
        </div>
      </template>

      <div class="data-configuration-erp-dropdown__panel">
        <div class="data-configuration-erp-dropdown__search-wrap">
          <img
            src="/img/icons/fbc-icons/SearchIcon.svg"
            alt=""
            aria-hidden="true"
            class="data-configuration-erp-dropdown__search-icon"
          />
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="data-configuration-erp-dropdown__search-input"
            placeholder="Search"
            :data-testid="`${dataTestid}__search`"
            @click.stop
            @mousedown.stop
          />
        </div>

        <div
          v-if="dropdownSections.aiSuggestions.length"
          class="data-configuration-erp-dropdown__section"
          :data-testid="`${dataTestid}__ai-section`"
        >
          <p class="data-configuration-erp-dropdown__section-title data-configuration-erp-dropdown__section-title--ai">
            ✦ AI SUGGESTIONS
          </p>
          <button
            v-for="(item, aiIndex) in dropdownSections.aiSuggestions"
            :key="`ai-${item.value}`"
            type="button"
            class="data-configuration-erp-dropdown__option"
            :class="{
              'data-configuration-erp-dropdown__option--highlight': aiIndex === 0,
              'data-configuration-erp-dropdown__option--active': value === item.value,
            }"
            :data-testid="`${dataTestid}__ai-option--${item.value}`"
            @click="selectItem(item.value)"
          >
            <span
              class="data-configuration-erp-dropdown__option-label"
              :class="{ 'data-configuration-erp-dropdown__option-label--strong': aiIndex === 0 }"
            >
              {{ item.value }}
            </span>
            <span
              class="data-configuration-erp-dropdown__match"
              :class="{
                'data-configuration-erp-dropdown__match--high': item.matchScore >= 80,
              }"
            >
              {{ item.matchScore }}% match
            </span>
          </button>
        </div>

        <div
          class="data-configuration-erp-dropdown__section data-configuration-erp-dropdown__section--list"
          :data-testid="`${dataTestid}__all-section`"
          @wheel.stop
        >
          <p class="data-configuration-erp-dropdown__section-title">ALL COLUMNS</p>
          <button
            v-for="item in dropdownSections.allColumns"
            :key="`all-${item.value}`"
            type="button"
            class="data-configuration-erp-dropdown__option"
            :class="{ 'data-configuration-erp-dropdown__option--active': value === item.value }"
            :data-testid="`${dataTestid}__option--${item.value}`"
            @click="selectItem(item.value)"
          >
            <span class="data-configuration-erp-dropdown__option-label">{{ item.value }}</span>
          </button>
          <p
            v-if="!dropdownSections.aiSuggestions.length && !dropdownSections.allColumns.length"
            class="data-configuration-erp-dropdown__empty"
            :data-testid="`${dataTestid}__empty`"
          >
            No columns match your search.
          </p>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
import { buildErpDropdownSections } from '@/modules/admin/utils/dataConfigurationUtils';
import { positionMenuNearActivator } from '@/utils/sync-attached-menu';

export default {
  name: 'DataConfigurationErpColumnDropdown',
  props: {
    value: { type: String, default: '' },
    destination: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    suggestedSource: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    placeholder: { type: String, default: 'Select ERP column' },
    dataTestid: { type: String, default: 'data-configuration-erp-dropdown' },
  },
  data() {
    return {
      menuOpen: false,
      menuWidth: 0,
      searchQuery: '',
      scrollCloseTargets: [],
      scrollCloseHandler: null,
    };
  },
  computed: {
    dropdownSections() {
      return buildErpDropdownSections({
        destination: this.destination,
        items: this.items,
        suggestedSource: this.suggestedSource,
        searchQuery: this.searchQuery,
        selectedValue: this.value,
      });
    },
  },
  watch: {
    menuOpen(isOpen) {
      if (isOpen) {
        this.updateMenuWidth();
        this.searchQuery = '';
        this.bindScrollClose();
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.pinMenuToField();
            const input = this.$refs.searchInput;
            if (input) {
              input.focus();
            }
          });
        });
        return;
      }
      this.unbindScrollClose();
      this.searchQuery = '';
    },
  },
  beforeDestroy() {
    this.unbindScrollClose();
    this.menuOpen = false;
    this.searchQuery = '';
  },
  methods: {
    collectScrollParents() {
      const parents = [];
      let el = this.$el?.parentElement;
      while (el && el !== document.documentElement) {
        const style = window.getComputedStyle(el);
        const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
        if (/(auto|scroll|overlay)/.test(overflow)) parents.push(el);
        el = el.parentElement;
      }
      return parents;
    },
    isScrollInsideMenu(event) {
      const target = event?.target;
      if (!target || target === document || target === window) return false;
      if (typeof target.closest !== 'function') return false;
      return !!target.closest('.data-configuration-erp-dropdown__menu');
    },
    bindScrollClose() {
      this.unbindScrollClose();
      const handler = (event) => {
        if (!this.menuOpen) return;
        if (this.isScrollInsideMenu(event)) return;
        this.menuOpen = false;
      };
      const targets = this.collectScrollParents();
      ['.v-data-table__wrapper', '.overflow-scroll'].forEach((selector) => {
        const extra = this.$el?.closest?.(selector);
        if (extra && !targets.includes(extra)) targets.push(extra);
      });
      targets.push(window);
      targets.forEach((target) => {
        target.addEventListener('scroll', handler, { passive: true, capture: true });
      });
      this.scrollCloseTargets = targets;
      this.scrollCloseHandler = handler;
    },
    unbindScrollClose() {
      if (!this.scrollCloseHandler) return;
      (this.scrollCloseTargets || []).forEach((target) => {
        target.removeEventListener('scroll', this.scrollCloseHandler, { capture: true });
      });
      this.scrollCloseTargets = [];
      this.scrollCloseHandler = null;
    },
    onActivatorKeydown(event, on) {
      if (this.disabled || this.readonly) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        on.click(event);
      }
    },
    pinMenuToField() {
      const activatorEl = this.$refs.activator;
      if (!activatorEl) return;
      positionMenuNearActivator({
        activatorEl,
        menuClass: 'data-configuration-erp-dropdown__menu',
        fieldRef: this.$refs.menu,
      });
      this.capMenuListHeight();
    },
    capMenuListHeight() {
      const menu = document.querySelector(
        '.data-configuration-erp-dropdown__menu.menuable__content__active'
      ) || document.querySelector('.data-configuration-erp-dropdown__menu');
      if (!menu) return;
      const list = menu.querySelector('.data-configuration-erp-dropdown__section--list');
      if (!list) return;
      const search = menu.querySelector('.data-configuration-erp-dropdown__search-wrap');
      const searchHeight = search?.offsetHeight || 52;
      const menuMax = parseFloat(menu.style.maxHeight) || 320;
      list.style.maxHeight = `${Math.max(120, menuMax - searchHeight - 24)}px`;
      list.style.overflowY = 'auto';
      list.style.overscrollBehavior = 'contain';
    },
    updateMenuWidth() {
      const field = this.$refs.activator;
      if (!field) return;
      this.menuWidth = Math.max(field.getBoundingClientRect().width, 280);
    },
    selectItem(value) {
      this.$emit('input', value);
      this.$emit('change', value);
      this.menuOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped src="./DataConfigurationErpColumnDropdown.scss"></style>
